import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { InsertNormativeBody } from "../../../../../http/patrol/types/normatives";
import { useNormativesApi } from "../../../../../http/patrol/useNormatives";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Textarea from "../../../../ui/Textarea";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface InsertNormativeForm {
  name: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  description: string | null;
}

const NewNormative: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const normativesApi = useNormativesApi();

  const onSubmit = () => async (formData: InsertNormativeForm) => {
    const body: InsertNormativeBody = {
      name: formData.name!,
      order: formData.order!,
      description: formData.description!,
      descriptionDe: null,
      descriptionEs: null,
      descriptionFr: null,
      descriptionIt: null,
      visibility: formData.visibility,
    };

    setIsSubmitting(true);

    try {
      const res = await normativesApi.insertAdminNormative(body);
      navigate(`/admin/systems/${systemId}/patrol_normatives/${res.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertNormativeForm>
      defaultValues={{
        name: null,
        description: null,
        order: 50,
        visibility: "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue }) => {
        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => (
                <Textarea
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("description", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              disabled={isSubmitting}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewNormative;
